import React, { useState, useCallback } from 'react';
import { getEntries } from '../../api/entries';
import { sendToWebhook } from '../../api/client';
import useApi from '../../hooks/useApi';
import TopBar from '../layout/TopBar';
import EntriesFilters from './EntriesFilters';
import EntryRow from './EntryRow';
import EntryDetailModal from './EntryDetailModal';
import Pagination from '../shared/Pagination';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import EmptyState from '../shared/EmptyState';

function EntriesView({ showToast }) {
  const [filters, setFilters] = useState({ search: '', type: '', classified: '', page: 1, limit: 15 });
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const apiFn = useCallback(() => {
    const params = { page: filters.page, limit: filters.limit };
    if (filters.search)     params.search = filters.search;
    if (filters.type)       params.type = filters.type;
    if (filters.classified !== '') params.classified = filters.classified;
    return getEntries(params);
  }, [filters]);

  const { data, meta, loading, error, refetch } = useApi(apiFn, [filters]);

  async function handleRetry(entry) {
    setRetryingId(entry.id);
    try {
      await sendToWebhook(entry.content);
      showToast?.('Reenviado a N8N', 'success');
      setTimeout(refetch, 4000);
    } catch (err) {
      showToast?.(`Error al reenviar: ${err.message}`, 'error');
    } finally {
      setRetryingId(null);
    }
  }

  const subtitle = meta ? `${meta.total} predicado${meta.total !== 1 ? 's' : ''}` : '';

  return (
    <div className="view-container">
      <TopBar title="Predicados" subtitle={subtitle}>
        <button className="btn-secondary" onClick={refetch}>↺</button>
      </TopBar>

      <div className="view-content">
        <EntriesFilters filters={filters} onChange={setFilters} />

        {loading && <LoadingState rows={8} />}
        {error && <ErrorState message={error} onRetry={refetch} />}

        {!loading && !error && data?.length === 0 && (
          <EmptyState
            icon="📨"
            title="Sin predicados"
            description="Aún no has enviado ningún predicado. Usa Nuevo para comenzar."
          />
        )}

        {!loading && !error && data?.length > 0 && (
          <>
            <div className="list-container">
              {data.map(entry => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  onClick={() => setSelectedEntryId(entry.id)}
                  onRetry={retryingId === entry.id ? null : handleRetry}
                />
              ))}
            </div>
            <Pagination
              page={filters.page}
              totalPages={meta?.totalPages || 1}
              onPageChange={p => setFilters(f => ({ ...f, page: p }))}
            />
          </>
        )}
      </div>

      <EntryDetailModal
        entryId={selectedEntryId}
        onClose={() => setSelectedEntryId(null)}
        showToast={showToast}
      />
    </div>
  );
}

export default EntriesView;
